/* Copyright (c) 2020-present CloudIO Inc */

import { ActionItemType } from '../../registry/Base.type';
import { isNotEmpty } from 'utils/isEmpty';
import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionMatchRecord';
import { loopTypes } from '../ActionLoopRecords/ActionLoopRecordsManager';

const ActionMatchRecordManager: ActionManagerFn<'ActionMatchRecord'> = (
  platform: Platform,
): ActionManager<'ActionMatchRecord'> => {
  return {
    ...baseActionManager(platform, 'ActionMatchRecord'),
    defaultValue,
    isChildAllowed: (page, item, childType) =>
      (
        ['ActionOnMatchFound', 'ActionOnMatchNotFound'] as ActionItemType[]
      ).includes(childType as ActionItemType),
    props: {
      ...baseActionProps,
      embeddedPageId: {
        label: 'Embedded Page ID',
        type: 'string',
      },
      alias: {
        label: 'Alias',
        type: 'DataSourceAliasChooser',
        required: true,
        pageIdProp: 'embeddedPageId',
        allowedTypes: [
          'CloudDataStore',
          'ReadOnlyDataStore',
          'DataStore',
          'LocalDataStore',
        ],
      },
      loop: {
        label: 'Loop',
        type: 'radio',
        options: loopTypes,
      },
      matchCondition: {
        label: 'Match Condition',
        type: 'expressionOnly',
        returnType: () => 'boolean',
      },
      matchFirst: {
        label: 'Match First',
        type: 'boolean',
      },
    },
    name: 'Match Record',
    isDataSourceAliasUsed: (item, alias) => item.props.alias === alias,
    getDisplayText: (item) => {
      let label = item.props.alias;
      if (isNotEmpty(item.props.matchCondition)) {
        label = `${label} - ${item.props.matchCondition}`;
      }
      return label;
    },
    description: `**Overview**<br/>
The Match Record action is a logic control component designed to search for specific records within a Data Store and execute different logic flows based on the result. It functions as a conditional "Find" operation, iterating through a dataset to verify if any records meet a defined criteria, and then branching execution depending on whether a match was discovered or not.<br/><br/>
This component serves as a parent container that strictly enforces the use of specific branching children: ActionOnMatchFound and ActionOnMatchNotFound. This structure allows developers to cleanly separate the logic for "Success" (record exists) and "Failure" (record missing) scenarios. It includes optimization features like matchFirst to halt the search immediately upon finding a result.

**Key Features**<br/>
* **Conditional Branching:** Routes execution to distinct child containers (On Match Found vs On Match Not Found) based on the search outcome, eliminating the need for complex "If/Else" variable checks.
* **Flexible Search Scope:** Utilizes a loop property to determine which subset of records to search through (e.g., 'All', 'Selected', 'Dirty').
* **Performance Optimization:** Features a matchFirst boolean property. When enabled, the action stops processing the moment the first matching record is found, which is ideal for simple existence checks.
* **Dynamic Criteria:** Uses a matchCondition expression to evaluate every record, allowing for complex, logic-driven search queries (e.g., #{item.age} > 18 && #{item.status} == 'Active').

**Properties**<br/>
* **Alias (Required):** A DataSourceAliasChooser property identifying the Data Store to search.
* **Loop:** A radio selection determining the scope of the search (e.g., All, Selected).
* **Match Condition:** An expression property returning a boolean.
  * *Function:* Evaluated for each record in the scope; if true, the record is considered a "match."
* **Match First:** A boolean property.
  * *Function:* If true, the search terminates after finding the single record.

**Use Cases**<br/>
* **Duplicate Check:** Searching a "Users" store for an existing email address before allowing a "Create User" action to proceed.
* **Validation:** Verifying that at least one "Manager" exists in a list of "Employees" before allowing a department to be saved.
* **Conditional UI:** Checking if a specific product ID exists in the "Cart" store to determine whether to show an "Add to Cart" or "Update Quantity" button.`,
  };
};

export default ActionMatchRecordManager;
